import React, { useEffect, useState } from 'react';
import { Button, Card, Tag, Tooltip, Progress, Modal, Form, DatePicker, Skeleton, Select, Input, message } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import { useProjectContext } from '../../../../core/components/projects/useProjectContext';
import { startCase } from 'lodash';
import moment from 'moment';
import { projectStatusColors, milestoneStatusOptions } from '../configs/project.config';
import AvatarS3 from '../../../shared/images/AvatarS3';
import AddTasksToMilestoneModal from './AddTasksToMilestoneModal';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';
import { getResponsiveBreakpoints } from '../utils/task.utils';

const { Option } = Select;

const ProjectMilestone = ({ currentProjectId }) => {
  const [isMilestoneModalVisible, setIsMilestoneModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneForm] = Form.useForm();
  const [expandedMilestoneId, setExpandedMilestoneId] = useState(null);
  const [isAddTaskToMilestoneModalVisible, setIsAddTaskToMilestoneModalVisible] = useState(false);
  const [selectedMilestoneForTasks, setSelectedMilestoneForTasks] = useState(null);
  const windowWidth = useWindowWidth();
  const { isMobile, isTablet, isDesktop } = getResponsiveBreakpoints(windowWidth);

  const {
    currentProject,
    fetchMilestones,
    milestones,
    milestonesLoading,
    createMilestone,
    createMilestoneLoading,
    updateMilestone,
    updateMilestoneLoading,
    refetchProject,
    updateProject,
  } = useProjectContext();

  useEffect(() => {
    if (currentProjectId) {
      fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  const handleCreateMilestone = () => {
    milestoneForm.resetFields();
    setEditingMilestone(null);
    setIsMilestoneModalVisible(true);
  };

  const handleEditMilestone = (milestone) => {
    setEditingMilestone(milestone);
    milestoneForm.setFieldsValue({
      name: milestone.name,
      description: milestone.description,
      startDate: milestone.startDate ? moment(milestone.startDate) : null,
      dueDate: milestone.dueDate ? moment(milestone.dueDate) : null,
      status: milestone.status || 'NOT_STARTED',
    });
    setIsMilestoneModalVisible(true);
  };

  const handleMilestoneModalOk = async () => {
    try {
      const values = await milestoneForm.validateFields();

      if (editingMilestone) {
        await updateMilestone({
          variables: {
            data: {
              name: values.name,
              description: values.description,
              startDate: values.startDate ? values.startDate.toISOString() : null,
              dueDate: values.dueDate ? values.dueDate.toISOString() : null,
              status: values.status,
            },
            where: { id: editingMilestone.id },
          },
        });
        message.success('Milestone updated successfully');
      } else {
        await createMilestone({
          variables: {
            data: {
              name: values.name,
              description: values.description,
              startDate: values.startDate ? values.startDate.toISOString() : null,
              dueDate: values.dueDate ? values.dueDate.toISOString() : null,
              status: values.status || 'NOT_STARTED',
              project: { connect: { id: currentProjectId } },
            },
          },
        });
        message.success('Milestone created successfully');
      }

      await fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });

      await refetchProject();

      setIsMilestoneModalVisible(false);
      milestoneForm.resetFields();
      setEditingMilestone(null);
    } catch (error) {
      console.error('Failed to save milestone:', error);
      message.error('Failed to save milestone');
    }
  };

  const handleDeleteMilestone = (milestone) => {
    Modal.confirm({
      title: 'Remove Milestone',
      content: 'Are you sure you want to remove this milestone from the project? Tasks will remain in the project.',
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const milestoneTaskIds = milestone.tasks?.map(t => t.id) || [];

          if (milestoneTaskIds.length > 0) {
            await updateMilestone({
              variables: {
                data: {
                  tasks: {
                    disconnect: milestoneTaskIds.map(id => ({ id })),
                  },
                },
                where: { id: milestone.id },
              },
            });
          }

          await updateProject({
            variables: {
              data: {
                milestones: {
                  disconnect: [{ id: milestone.id }],
                },
              },
              where: { id: currentProjectId },
            },
          });

          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
              orderBy: 'dueDate_ASC',
            },
          });

          await refetchProject();

          message.success('Milestone removed from project successfully');
        } catch (error) {
          console.error('Failed to remove milestone:', error);
          message.error('Failed to remove milestone');
        }
      },
    });
  };

  const handleAddTasksToMilestone = (milestone) => {
    setSelectedMilestoneForTasks(milestone);
    setIsAddTaskToMilestoneModalVisible(true);
  };

  const handleAddTasksToMilestoneConfirm = async (taskIds) => {
    if (!selectedMilestoneForTasks || taskIds.length === 0) {
      message.warning('Please select at least one task');
      return;
    }

    try {
      const currentMilestoneTaskIds = selectedMilestoneForTasks.tasks?.map(t => t.id) || [];
      const tasksToConnect = taskIds.filter(id => !currentMilestoneTaskIds.includes(id));

      if (tasksToConnect.length === 0) {
        message.info('Selected tasks are already in this milestone');
        setIsAddTaskToMilestoneModalVisible(false);
        return;
      }

      await updateMilestone({
        variables: {
          data: {
            tasks: {
              connect: tasksToConnect.map(id => ({ id })),
            },
          },
          where: { id: selectedMilestoneForTasks.id },
        },
      });

      await fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });

      await refetchProject();

      message.success(`Successfully added ${tasksToConnect.length} task(s) to milestone`);
      setIsAddTaskToMilestoneModalVisible(false);
      setSelectedMilestoneForTasks(null);
    } catch (error) {
      console.error('Failed to add tasks to milestone:', error);
      message.error('Failed to add tasks to milestone');
    }
  };

  const handleRemoveTaskFromMilestone = (milestone, task) => {
    Modal.confirm({
      title: 'Remove Task from Milestone',
      content: `Are you sure you want to remove "${task.name}" from this milestone? The task will remain in the project.`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await updateMilestone({
            variables: {
              data: {
                tasks: {
                  disconnect: [{ id: task.id }],
                },
              },
              where: { id: milestone.id },
            },
          });

          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
              orderBy: 'dueDate_ASC',
            },
          });

          await refetchProject();

          message.success('Task removed from milestone successfully');
        } catch (error) {
          console.error('Failed to remove task from milestone:', error);
          message.error('Failed to remove task from milestone');
        }
      },
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleOutlined style={{ fontSize: 20, color: '#10b981' }} />;
      case 'IN_PROGRESS':
        return <ClockCircleOutlined style={{ fontSize: 20, color: '#3b82f6' }} />;
      case 'NOT_STARTED':
        return <FlagOutlined style={{ fontSize: 20, color: '#94a3b8' }} />;
      default:
        return <ClockCircleOutlined style={{ fontSize: 20, color: '#94a3b8' }} />;
    }
  };

  if (milestonesLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {[1, 2, 3].map((item) => (
          <Card
            key={item}
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Skeleton active paragraph={{ rows: 3 }} />
          </Card>
        ))}
      </div>
    );
  }

  if (!milestones || milestones.length === 0) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button
            type="primary"
            style={{
              fontSize: '14px',
              height: '40px',
              borderRadius: '5px',
              backgroundColor: '#3b82f6',
              width: isMobile || isTablet ? '100%' : 'auto',
            }}
            onClick={handleCreateMilestone}
            loading={createMilestoneLoading}
          >
            Create Milestone
          </Button>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Text style={{ color: '#94a3b8', fontSize: '16px' }}>No milestones available</Text>
        </div>

        {/* Milestone Modal */}
        <Modal
          title={editingMilestone ? 'Edit Milestone' : 'Create Milestone'}
          open={isMilestoneModalVisible}
          onOk={handleMilestoneModalOk}
          onCancel={() => {
            setIsMilestoneModalVisible(false);
            milestoneForm.resetFields();
            setEditingMilestone(null);
          }}
          okText={editingMilestone ? 'Update' : 'Create'}
          confirmLoading={createMilestoneLoading || updateMilestoneLoading}
          width={600}
        >
          <Form
            form={milestoneForm}
            layout="vertical"
            name="milestoneForm"
          >
            <Form.Item
              label="Milestone Name"
              name="name"
              rules={[{ required: true, message: 'Please enter milestone name' }]}
            >
              <Input placeholder="Enter milestone name" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
            >
              <Input.TextArea
                placeholder="Enter milestone description"
                rows={3}
              />
            </Form.Item>

            <Form.Item
              label="Start Date"
              name="startDate"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const dueDate = getFieldValue('dueDate');
                    if (!value || !dueDate || value.isBefore(dueDate, 'day')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Start date must be before due date'));
                  },
                }),
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                onChange={() => {
                  milestoneForm.validateFields(['dueDate']);
                }}
              />
            </Form.Item>

            <Form.Item
              label="Due Date"
              name="dueDate"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue('startDate');
                    if (!value || !startDate || value.isAfter(startDate, 'day')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Due date must be after start date'));
                  },
                }),
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                onChange={() => {
                  milestoneForm.validateFields(['startDate']);
                }}
              />
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              initialValue="NOT_STARTED"
            >
              <Select>
                {milestoneStatusOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button
          type="primary"
          style={{
            fontSize: '14px',
            height: '40px',
            borderRadius: '5px',
            backgroundColor: '#3b82f6',
            width: isMobile || isTablet ? '100%' : 'auto',
          }}
          onClick={handleCreateMilestone}
          loading={createMilestoneLoading}
        >
          Create Milestone
        </Button>
      </div>

      <div>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile || isTablet ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile || isTablet ? 'flex-start' : 'center',
            marginBottom: 20,
            gap: isMobile || isTablet ? '8px' : '0',
          }}
        >
          <Text strong style={{ fontSize: isMobile ? '18px' : '20px', color: '#1f2937' }}>Project Milestones</Text>
          <Text style={{ fontSize: '14px', color: '#64748b' }}>{milestones.length} milestone{milestones.length > 1 ? 's' : ''}</Text>
        </div>

        {/* Milestone Cards Grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {milestones.map(milestone => {
            const milestoneTasks = milestone.tasks || [];
            const completedTasks = milestoneTasks.filter(t => t.status === 'COMPLETED' || t.status === 'DONE').length;
            const progressPercent = milestoneTasks.length > 0 ? Math.round((completedTasks / milestoneTasks.length) * 100) : 0;
            const isExpanded = expandedMilestoneId === milestone.id;

            const today = moment();
            const dueDate = milestone.dueDate ? moment(milestone.dueDate) : null;
            const daysLeft = dueDate ? dueDate.diff(today, 'days') : null;

            let dueDateColor = '#64748b';
            if (daysLeft !== null) {
              if (daysLeft < 0) {
                dueDateColor = '#ef4444';
              } else if (daysLeft <= 3) {
                dueDateColor = '#f59e0b';
              }
            }

            const availableTasks = (currentProject?.tasks || []).filter(
              task => !milestoneTasks.some(mt => mt.id === task.id)
            );

            return (
              <Card
                key={milestone.id}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
                hoverable
                bodyStyle={{ padding: '16px' }}
                onClick={() => setExpandedMilestoneId(isExpanded ? null : milestone.id)}
              >
                {/* Action Buttons */}
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    display: 'flex',
                    gap: 4,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditMilestone(milestone);
                    }}
                    style={{ padding: '4px' }}
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMilestone(milestone);
                    }}
                    style={{ padding: '4px' }}
                  />
                </div>

                {/* Status Icon & Title */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12, paddingRight: 60 }}>
                  {getStatusIcon(milestone.status)}
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: '16px', color: '#1f2937', display: 'block', lineHeight: '24px' }}>
                      {milestone.name}
                    </Text>
                  </div>
                </div>

                {/* Start Date and Due Date */}
                {(milestone.startDate || dueDate) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                    {milestone.startDate && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarOutlined style={{ color: '#3b82f6', fontSize: 14 }} />
                        <Text style={{ fontSize: '14px', color: '#64748b' }}>
                          <Text strong style={{ fontSize: '12px', color: '#94a3b8', marginRight: 6 }}>START:</Text>
                          {moment(milestone.startDate).format('MMM D, YYYY')}
                        </Text>
                      </div>
                    )}
                    {dueDate && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarOutlined style={{ color: dueDateColor, fontSize: 14 }} />
                        <Text style={{ fontSize: '14px', color: dueDateColor }}>
                          <Text strong style={{ fontSize: '12px', color: '#94a3b8', marginRight: 6 }}>DUE:</Text>
                          {dueDate.format('MMM D, YYYY')}
                        </Text>
                      </div>
                    )}
                  </div>
                )}

                {/* Task Count */}
                <div style={{ marginBottom: 12 }}>
                  <Text style={{ fontSize: '14px', color: '#64748b' }}>
                    {milestoneTasks.length} task{milestoneTasks.length !== 1 ? 's' : ''}
                    {milestoneTasks.length > 0 && ` · ${completedTasks} completed`}
                  </Text>
                </div>

                {/* Progress Bar */}
                <Progress
                  percent={progressPercent}
                  strokeColor={{
                    '0%': '#3b82f6',
                    '100%': '#10b981',
                  }}
                  showInfo={false}
                  size="small"
                  style={{ marginBottom: 8 }}
                />

                {/* Expanded Content */}
                {isExpanded && (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: '1px solid #e5e7eb',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                        Tasks
                      </Text>
                      {availableTasks.length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => handleAddTasksToMilestone(milestone)}
                          style={{ padding: 0, height: 'auto' }}
                        >
                          Add
                        </Button>
                      )}
                    </div>

                    {milestoneTasks.length === 0 ? (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '20px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '6px',
                          border: '1px dashed #cbd5e1',
                        }}
                      >
                        <Text style={{ color: '#94a3b8', fontSize: '13px' }}>
                          No tasks yet
                        </Text>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {milestoneTasks.map(task => (
                          <div
                            key={task.id}
                            style={{
                              padding: '12px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb',
                              transition: 'all 0.2s ease',
                              position: 'relative',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f1f5f9';
                              e.currentTarget.style.borderColor = '#cbd5e1';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.borderColor = '#e5e7eb';
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: isMobile || isTablet ? 'column' : 'row',
                                alignItems: isMobile || isTablet ? 'flex-start' : 'center',
                                gap: isMobile || isTablet ? '12px' : '16px',
                              }}
                            >
                              <Link
                                to={`/tasks/details/${task.id}`}
                                style={{
                                  textDecoration: 'none',
                                  display: 'flex',
                                  flexDirection: isMobile || isTablet ? 'column' : 'row',
                                  flex: 1,
                                  alignItems: isMobile || isTablet ? 'flex-start' : 'center',
                                  gap: isMobile || isTablet ? '8px' : '16px',
                                  width: isMobile || isTablet ? '100%' : 'auto',
                                }}
                              >
                                {/* Task Name Column */}
                                <div style={{
                                  flex: isMobile || isTablet ? '1' : '1 1 30%',
                                  minWidth: 0,
                                  width: isMobile || isTablet ? '100%' : 'auto',
                                  paddingRight: isMobile || isTablet ? '40px' : '0',
                                }}>
                                  <Text
                                    strong
                                    style={{
                                      fontSize: '13px',
                                      color: '#1f2937',
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {task.name}
                                  </Text>
                                  {task.requestType && (
                                    <Text style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginTop: 2 }}>
                                      {task.requestType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                                    </Text>
                                  )}
                                </div>

                                {isMobile || isTablet ? (
                                  // Mobile layout - stack all info vertically
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                                    {/* Assignees */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Text style={{ fontSize: '11px', color: '#94a3b8', minWidth: 80 }}>Assignees:</Text>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {task.assigneeUsers && task.assigneeUsers.length > 0 ? (
                                          <>
                                            {task.assigneeUsers.slice(0, 3).map((user, idx) => (
                                              <Tooltip
                                                key={user.id || idx}
                                                title={startCase(user.name || user.username)}
                                              >
                                                <div style={{ display: 'inline-flex' }}>
                                                  <AvatarS3
                                                    imgKey={user.avatar && user.avatar.key}
                                                    username={user.name || user.username}
                                                    avatarProps={{ size: 24 }}
                                                  />
                                                </div>
                                              </Tooltip>
                                            ))}
                                            {task.assigneeUsers.length > 3 && (
                                              <Text style={{ fontSize: '11px', color: '#64748b' }}>
                                                +{task.assigneeUsers.length - 3}
                                              </Text>
                                            )}
                                          </>
                                        ) : (
                                          <Text style={{ fontSize: '11px', color: '#94a3b8' }}>-</Text>
                                        )}
                                      </div>
                                    </div>

                                    {/* Channel Partner */}
                                    {task.channelPartner && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Text style={{ fontSize: '11px', color: '#94a3b8', minWidth: 80 }}>Partner:</Text>
                                        <Text style={{ fontSize: '11px', color: '#64748b' }}>
                                          {task.channelPartner.name}
                                        </Text>
                                      </div>
                                    )}

                                    {/* Advertiser */}
                                    {task.advertiser && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Text style={{ fontSize: '11px', color: '#94a3b8', minWidth: 80 }}>Advertiser:</Text>
                                        <Text style={{ fontSize: '11px', color: '#64748b' }}>
                                          {task.advertiser.name}
                                        </Text>
                                      </div>
                                    )}

                                    {/* Status */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Text style={{ fontSize: '11px', color: '#94a3b8', minWidth: 80 }}>Status:</Text>
                                      <Tag
                                        color={projectStatusColors[task.status] || 'default'}
                                        style={{ margin: 0, fontSize: '11px' }}
                                      >
                                        {task.status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'N/A'}
                                      </Tag>
                                    </div>
                                  </div>
                                ) : (
                                  // Desktop layout - horizontal
                                  <>
                                    {/* Assignees Column */}
                                    <div style={{ flex: '0 0 120px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                      {task.assigneeUsers && task.assigneeUsers.length > 0 ? (
                                        <>
                                          {task.assigneeUsers.slice(0, 3).map((user, idx) => (
                                            <Tooltip
                                              key={user.id || idx}
                                              title={startCase(user.name || user.username)}
                                            >
                                              <div style={{ display: 'inline-flex' }}>
                                                <AvatarS3
                                                  imgKey={user.avatar && user.avatar.key}
                                                  username={user.name || user.username}
                                                  avatarProps={{ size: 24 }}
                                                />
                                              </div>
                                            </Tooltip>
                                          ))}
                                          {task.assigneeUsers.length > 3 && (
                                            <Text style={{ fontSize: '11px', color: '#64748b' }}>
                                              +{task.assigneeUsers.length - 3}
                                            </Text>
                                          )}
                                        </>
                                      ) : (
                                        <Text style={{ fontSize: '11px', color: '#94a3b8' }}>-</Text>
                                      )}
                                    </div>

                                    {/* Channel Partner Column */}
                                    <div style={{ flex: '0 0 140px', minWidth: 0 }}>
                                      {task.channelPartner ? (
                                        <Tooltip title={task.channelPartner.name}>
                                          <Text
                                            style={{
                                              fontSize: '11px',
                                              color: '#64748b',
                                              display: 'block',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {task.channelPartner.name}
                                          </Text>
                                        </Tooltip>
                                      ) : (
                                        <Text style={{ fontSize: '11px', color: '#94a3b8' }}>-</Text>
                                      )}
                                    </div>

                                    {/* Advertiser Column */}
                                    <div style={{ flex: '0 0 140px', minWidth: 0 }}>
                                      {task.advertiser ? (
                                        <Tooltip title={task.advertiser.name}>
                                          <Text
                                            style={{
                                              fontSize: '11px',
                                              color: '#64748b',
                                              display: 'block',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {task.advertiser.name}
                                          </Text>
                                        </Tooltip>
                                      ) : (
                                        <Text style={{ fontSize: '11px', color: '#94a3b8' }}>-</Text>
                                      )}
                                    </div>

                                    {/* Status Column */}
                                    <div style={{ flex: '0 0 auto' }}>
                                      <Tag
                                        color={projectStatusColors[task.status] || 'default'}
                                        style={{ margin: 0, fontSize: '11px' }}
                                      >
                                        {task.status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'N/A'}
                                      </Tag>
                                    </div>
                                  </>
                                )}
                              </Link>

                              {/* Actions Column */}
                              <Tooltip title="Remove from milestone">
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveTaskFromMilestone(milestone, task);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    alignSelf: isMobile || isTablet ? 'flex-end' : 'center',
                                    position: isMobile || isTablet ? 'absolute' : 'relative',
                                    top: isMobile || isTablet ? '12px' : 'auto',
                                    right: isMobile || isTablet ? '12px' : 'auto',
                                  }}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Milestone Modal */}
      <Modal
        title={editingMilestone ? 'Edit Milestone' : 'Create Milestone'}
        open={isMilestoneModalVisible}
        onOk={handleMilestoneModalOk}
        onCancel={() => {
          setIsMilestoneModalVisible(false);
          milestoneForm.resetFields();
          setEditingMilestone(null);
        }}
        okText={editingMilestone ? 'Update' : 'Create'}
        confirmLoading={createMilestoneLoading || updateMilestoneLoading}
        width={600}
      >
        <Form
          form={milestoneForm}
          layout="vertical"
          name="milestoneForm"
        >
          <Form.Item
            label="Milestone Name"
            name="name"
            rules={[{ required: true, message: 'Please enter milestone name' }]}
          >
            <Input placeholder="Enter milestone name" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea
              placeholder="Enter milestone description"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label="Start Date"
            name="startDate"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const dueDate = getFieldValue('dueDate');
                  if (!value || !dueDate || value.isBefore(dueDate, 'day')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Start date must be before due date'));
                },
              }),
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              onChange={() => {
                milestoneForm.validateFields(['dueDate']);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Due Date"
            name="dueDate"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue('startDate');
                  if (!value || !startDate || value.isAfter(startDate, 'day')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Due date must be after start date'));
                },
              }),
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              onChange={() => {
                milestoneForm.validateFields(['startDate']);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            initialValue="NOT_STARTED"
          >
            <Select>
              {milestoneStatusOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Tasks to Milestone Modal */}
      <AddTasksToMilestoneModal
        visible={isAddTaskToMilestoneModalVisible}
        onCancel={() => {
          setIsAddTaskToMilestoneModalVisible(false);
          setSelectedMilestoneForTasks(null);
        }}
        onConfirm={handleAddTasksToMilestoneConfirm}
        milestone={selectedMilestoneForTasks}
        projectTasks={currentProject?.tasks || []}
        loading={updateMilestoneLoading}
      />
    </>
  );
};

export default ProjectMilestone;
